import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../../../api/axiosInstance';
import { toast } from 'react-toast';

/**
 * RunDiscoveryTestCaseModal
 * Simplified variant of RunTestCaseModal for the UI Discovery flow.
 * No environment selection.
 * - Server mode calls POST /crawler/execute with headless=true.
 * - Local mode posts TRIGGER_LOCAL_EXECUTION to the Robitest agent and waits
 *   for an EXECUTION_SUCCESS / EXECUTION_ERROR message in response.
 */
const RunDiscoveryTestCaseModal = ({
  isOpen,
  onClose,
  onSuccess,
  testCaseId,
  testCaseName,
}) => {
  const [selectedMode, setSelectedMode] = useState('local');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedMode('local');
    setError(null);
    setResult(null);
    setShowLogs(false);
  }, [isOpen]);

  // True while waiting for the local agent to respond to TRIGGER_LOCAL_EXECUTION
  const awaitingLocalRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleAgentMessage = (event) => {
      const msg = event.data;
      if (msg?.source !== 'ROBITEST_AGENT' || !awaitingLocalRef.current) return;

      if (msg.type === 'EXECUTION_SUCCESS') {
        const data = msg.payload?.data || {};
        if (data.test_case_id != null && String(data.test_case_id) !== String(testCaseId)) return;

        awaitingLocalRef.current = false;
        setResult({
          status: data.status,
          duration_seconds: data.duration,
          logs: data.logs,
          error_message: data.error_message,
        });
        setIsLoading(false);

        if (data.status === 'failed') {
          toast.error(data.error_message || msg.payload?.message || 'Test execution failed');
        } else {
          toast.success(msg.payload?.message || 'Test execution finished');
        }
        onSuccess?.(msg);
      } else if (msg.type === 'EXECUTION_ERROR') {
        awaitingLocalRef.current = false;
        const errorMessage = msg.error || 'Local execution failed';
        setError(errorMessage);
        setIsLoading(false);
        toast.error(errorMessage);
      }
    };

    window.addEventListener('message', handleAgentMessage);
    return () => {
      window.removeEventListener('message', handleAgentMessage);
      awaitingLocalRef.current = false;
    };
  }, [isOpen, testCaseId, onSuccess]);

  const handleRun = async () => {
    setIsLoading(true);
    setError(null);
    setResult(null);

    if (selectedMode === 'local') {
      awaitingLocalRef.current = true;
      window.postMessage(
        {
          source: 'ROBITEST_CLOUD',
          action: 'TRIGGER_LOCAL_EXECUTION',
          testCaseId: testCaseId,
          headless: false,
          autoHeal: true,
        },
        '*'
      );
      return;
    }

    try {
      const response = await axiosInstance.post(
        '/crawler/execute',
        null,
        { params: { test_case_id: testCaseId, headless: true } }
      );

      setResult(response?.data || null);
      onSuccess?.(response);
    } catch (err) {
      console.error('Error executing test case:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to execute test case';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    awaitingLocalRef.current = false;
    setIsLoading(false);
    setSelectedMode('local');
    setError(null);
    setResult(null);
    setShowLogs(false);
    onClose();
  };

  if (!isOpen) return null;

  const isPassed = result?.status === 'passed';
  const isFailed = result?.status === 'failed';

  return (
    <div data-rt-modal-overlay className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 13000 }} onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.querySelector('[data-rt-modal-close], .close-btn, .modal-close, button[aria-label="Close"], button[aria-label="close"]'); if (b) b.click(); } }}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200" style={{ zIndex: 14000 }}>
        {/* Header */}
        <div className="rt-modal__header flex-shrink-0">
          <div>
            <h2>Run Test Case: {testCaseName}</h2>
            <p>Choose an execution mode and run this test case</p>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Execution Mode Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-700">
              Execution Mode <span className="text-red-500">*</span>
            </label>

            <label
              className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedMode === 'local'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-gray-50 hover:border-blue-300'
              }`}
            >
              <input
                type="radio"
                name="discoveryExecutionMode"
                value="local"
                checked={selectedMode === 'local'}
                onChange={(e) => {
                  setSelectedMode(e.target.value);
                  setError(null);
                }}
                className="w-4 h-4 text-blue-600 cursor-pointer"
                disabled={isLoading}
              />
              <div className="ml-3 flex-1">
                <p className="font-medium text-gray-700">Local Machine</p>
                <p className="text-xs text-gray-600 mt-1">
                  Run with the browser visible (headless: false)
                </p>
              </div>
              <svg className="w-6 h-6 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </label>

            <label
              className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedMode === 'server'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-gray-50 hover:border-blue-300'
              }`}
            >
              <input
                type="radio"
                name="discoveryExecutionMode"
                value="server"
                checked={selectedMode === 'server'}
                onChange={(e) => {
                  setSelectedMode(e.target.value);
                  setError(null);
                }}
                className="w-4 h-4 text-blue-600 cursor-pointer"
                disabled={isLoading}
              />
              <div className="ml-3 flex-1">
                <p className="font-medium text-gray-700">Server (Headless)</p>
                <p className="text-xs text-gray-600 mt-1">
                  Run on server infrastructure in headless mode (headless: true)
                </p>
              </div>
              <svg className="w-6 h-6 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 flex items-start gap-2">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </p>
            </div>
          )}

          {/* Result */}
          {result && (
            <div
              className={`p-4 rounded-lg border-2 ${
                isPassed ? 'bg-green-50 border-green-200' : isFailed ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`text-sm font-semibold uppercase ${
                    isPassed ? 'text-green-700' : isFailed ? 'text-red-700' : 'text-gray-700'
                  }`}
                >
                  {result.status || 'Unknown'}
                </span>
                {typeof result.duration_seconds === 'number' && (
                  <span className="text-xs text-gray-500">{result.duration_seconds}s</span>
                )}
              </div>

              {result.error_message && (
                <p className="text-xs text-red-700 mt-2 break-words">{result.error_message}</p>
              )}

              {result.logs && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => setShowLogs((prev) => !prev)}
                    className="text-xs font-medium text-blue-600 hover:underline"
                  >
                    {showLogs ? 'Hide logs' : 'Show logs'}
                  </button>
                  {showLogs && (
                    <pre className="mt-2 p-2 bg-gray-900 text-gray-100 text-xs rounded max-h-48 overflow-auto whitespace-pre-wrap">
                      {result.logs}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={isLoading && selectedMode === 'server'}
            className="px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={handleRun}
            disabled={isLoading}
            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isLoading ? 'Running...' : result ? 'Run Again' : 'Run Test Case'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RunDiscoveryTestCaseModal;
